import React from 'react';
import { useWallet } from '../contexts/WalletContext';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  WalletIcon,
  CalendarDaysIcon,
  CpuChipIcon,
  ArrowRightIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import Button from '../components/common/Button';

const ProfilePage = () => {
  const { currentAccount, userProfile } = useWallet();

  if (!currentAccount) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
        <UserCircleIcon className="h-24 w-24 text-slate-600 mb-4" />
        <h2 className="text-2xl font-bold text-slate-200 mb-2">Wallet Not Connected</h2>
        <p className="text-slate-400 mb-6 max-w-md">
          Please connect your wallet to view your profile information and manage your agents.
        </p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
            My Profile
          </h1>
          <p className="text-slate-400 mt-1">
            Manage your account settings and view your activity.
          </p>
        </div>

      </div>

      {/* Profile Card */}
      <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 sm:p-8 shadow-xl">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
            <UserCircleIcon className="h-16 w-16 text-white" />
          </div>
          
          <div className="flex-1 space-y-4 w-full">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {userProfile?.display_name || 'Unnamed User'}
              </h2>
              <p className="text-slate-400 text-sm flex items-center mt-1">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                Active
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center text-slate-400 mb-1">
                  <WalletIcon className="h-4 w-4 mr-2" />
                  <span className="text-xs font-medium uppercase tracking-wider">Wallet Address</span>
                </div>
                <div className="font-mono text-slate-200 break-all">
                  {currentAccount}
                </div>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center text-slate-400 mb-1">
                  <CalendarDaysIcon className="h-4 w-4 mr-2" />
                  <span className="text-xs font-medium uppercase tracking-wider">Joined Since</span>
                </div>
                <div className="text-slate-200">
                  {userProfile?.created_at ? formatDate(userProfile.created_at) : 'Just now'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions / Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/my-agents" className="group">
          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:bg-slate-800/80 transition-all duration-300 h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/10 rounded-lg text-blue-400 group-hover:bg-blue-500/20 transition-colors">
                <CpuChipIcon className="h-6 w-6" />
              </div>
              <ArrowRightIcon className="h-5 w-5 text-slate-500 group-hover:text-blue-400 transition-colors" />
            </div>
            <h3 className="text-lg font-semibold text-slate-200 mb-1">My Agents</h3>
            <p className="text-slate-400 text-sm">
              View and manage your deployed AI agents.
            </p>
          </div>
        </Link>

        <Link to="/create-agent" className="group">
          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:bg-slate-800/80 transition-all duration-300 h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500/10 rounded-lg text-purple-400 group-hover:bg-purple-500/20 transition-colors">
                <UserCircleIcon className="h-6 w-6" />
              </div>
              <ArrowRightIcon className="h-5 w-5 text-slate-500 group-hover:text-purple-400 transition-colors" />
            </div>
            <h3 className="text-lg font-semibold text-slate-200 mb-1">Create New Agent</h3>
            <p className="text-slate-400 text-sm">
              Launch a new AI agent on the marketplace.
            </p>
          </div>
        </Link>

        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 opacity-75 cursor-not-allowed h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg text-green-400">
              <WalletIcon className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium bg-slate-700 text-slate-300 px-2 py-1 rounded">Coming Soon</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-200 mb-1">Earnings</h3>
          <p className="text-slate-400 text-sm">
            Track your earnings from agent interactions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
